import React, { useState, useEffect, useMemo } from "react";
import styles from "./CodigoFin.module.css";
import img from "../../../resources/img/elcodigo.webp";
import { authCursoLogic, ClaseData } from "../../../../Logic/AuthCurso";
import { c7Api } from "../../../../Logic/c7Api";


interface CodigoFinProps {
  onBack: () => void; // Prop para regresar
  periodo: string; //periodo en que el usuario se inscribio para el curso, a partir de ahi se cuenta el progreso del curso
}

// Props del hijo
interface AccordionProps {
  index: number;
  data: ClaseData;
  status: "locked_past" | "active" | "future";
}

// --- Componente de Acordeón Individual ---
const ClassAccordion: React.FC<AccordionProps> = ({ index, data, status }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Solo permitimos abrir si es la clase ACTIVA (Semana actual)
  const handleToggle = () => {
    if (status === "active") {
      setIsOpen(!isOpen);
    }
  };

  // Determinar clases CSS según el estado (Pasado vs Actual)
  const isPast = status === "locked_past";
  
  return (
    <div className={styles.accordionWrapper}>
      {/* Cabecera */}
      <div 
        className={`${styles.accordionHeader} ${isPast ? styles.locked : ""}`} 
        onClick={handleToggle}
      >
        <div className={styles.leftContent}>
          <div className={styles.thumbnailIcon}>
            <img src={img} alt="thumb" style={isPast ? {filter: 'grayscale(100%)'} : {}} />
          </div>
          <div className={styles.textInfo}>
            <h4>{data.title.toUpperCase()}</h4>
          </div>
        </div>

        <div className={styles.rightContent}>
          {/* Si ya pasó la semana (isPast), sale checkeado siempre. Si es actual, sale vacío */}
          <div 
            className={`${styles.statusIcon} ${isPast ? styles.autoChecked : ""}`}
          >
            {isPast && "✔"}
          </div>
          
          {/* Flecha (Solo si es interactivo) */}
          {!isPast && (
            <div className={`${styles.arrowIcon} ${isOpen ? styles.rotated : ""} ${isPast ? styles.hidden : ""}`}>
              ▼
            </div>
          )}
        </div>
      </div>

      {/* Cuerpo Desplegable (Solo abre si está activo) */}
      {isOpen && !isPast && (
        <div className={styles.accordionBody}>
          <div className={styles.videoContainer}>
            <iframe
              width="100%"
              height="315"
              src={ index == 0 ? `https://www.youtube.com/embed/${data.youtubeid}` :`https://player.vimeo.com/video/${data.youtubeid}?h=0&badge=0&autopause=0&player_id=0&app_id=58479`}
              title="Vimeo video player"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>

          {/* Botón de descarga con estilos opcionales */}
          <div className={styles.materialContainer}>
            <a 
              href={c7Api.getBaseUrl() + ( c7Api.getBaseUrl().includes("127.0.0.1") ? data.pdfName : "/Server/public" + data.pdfName)} 
              download={data.title + ".pdf"}        
              target="_blank"           // Abre en pestaña nueva
              rel="noopener noreferrer" // Seguridad para pestañas nuevas       
              className={styles.downloadButton}
            >
              <span role="img" aria-label="pdf">📄</span> Descargar clase complementaria
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Componente Principal ---
const CodigoFin: React.FC<CodigoFinProps> = ({ onBack, periodo }) => {
  
  const [currentWeek, setCurrentWeek] = useState(0);
  const [allClasses, setAllClasses] = useState<ClaseData[]>([]);

  // Cálculo de Fecha al montar el componente
  useEffect(() => {

    // Lista de todas las clases
    (async function() { 
      await authCursoLogic.GetAllClassesCurso().then((data) => {
          setAllClasses(data);
      });
    })();

    const now = new Date(); // Fecha actual

    // --- CONFIGURACIÓN ---
    // Fecha de inicio: 12 de Enero 2026 (o el que definas)
    let START_DATE = new Date("2026-01-12T00:00:00"); //default 

    if(periodo === "Enero 2026")
       START_DATE = new Date("2026-01-12T00:00:00"); 
      else  if(periodo === "Abril 2026")
               START_DATE = new Date("2026-04-13T00:00:00"); 
    
    // Si la fecha actual es ANTES del inicio, estamos en la semana 0 (o -1)
    if (now < START_DATE) {
        setCurrentWeek(0); // Muestra la primera clase lista para arrancar
        return;
    }

    // Diferencia en milisegundos
    const diffTime = Math.abs(now.getTime() - START_DATE.getTime());
    // Convertir a semanas (1000ms * 60s * 60m * 24h * 7d)
    const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
    
    setCurrentWeek(diffWeeks); 
  }, []);

  // Filtrar: Solo mostramos clases hasta la semana actual
  // (+1 porque el index es 0, pero queremos ver la clase 1)
  const visibleClasses = allClasses.slice(0, currentWeek + 1);
  
  // 2. Le damos la vuelta para que la más reciente quede en la posición 0 (arriba)
  const reversedClasses = [...visibleClasses].reverse();

  // Porcentaje basado en el tiempo transcurrido (Semanas pasadas / Total clases)
  const progressPercentage = Math.min(Math.round((currentWeek / allClasses.length) * 100), 100);

  return (
    <div className={styles.coursePageContainer}>
      
      {/* --- BOTÓN FLOTANTE DE REGRESO --- */}
      <button className={styles.floatingBackButton} onClick={onBack}>
         Regresar
      </button>

      {/* Sección Superior */}
      <div className={styles.topSection}>
        <div className={styles.courseSummaryCard}>
          <div className={styles.tapeDeco}></div>

          <div className={styles.cardContent}>
            <div className={styles.infoColumn}>
              <h1>El CÓDIGO DEL FIN</h1>
              <p>
               Este curso dura 1 año. Cada semana se desbloqueará una nueva enseñanza 
               automáticamente. Las clases anteriores se marcarán como completadas.
              </p>
              
              {/* Barra de Progreso */}
              <div className={styles.progressContainer}>
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill} 
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className={styles.progressLabels}>
                  <span>Inicio</span>
                  <span style={{fontSize: '0.8rem'}}>Semana {currentWeek + 1} de {allClasses.length}</span>
                  <span>Fin</span>
                </div>
              </div>
            </div>

            <div className={styles.imageColumn}>
              <img src={img} alt="Curso" />
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Clases */}
      <div className={styles.classesListContainer}>
        {/* Mensaje al inicio de la lista */}
        <div style={{textAlign: 'center', marginTop: '40px', color: '#666', fontStyle: 'italic'}}>
            Próxima clase disponible en {8 - (new Date().getDay() === 0 ? 7 : new Date().getDay())} días...
        </div>

       {reversedClasses.map((clase, index) => { 
          // LA SOLUCIÓN: Calculamos su semana real basándonos en su posición en la lista visible, 
          // ignorando por completo qué "id" le haya asignado la base de datos.
          const originalIndex = visibleClasses.length - 1 - index; 
          
          let status: "locked_past" | "active" | "future" = "future";
          
          if (originalIndex < currentWeek) {
             status = "locked_past"; // Semanas anteriores (Bloqueadas, grises y con Check)
          } else if (originalIndex === currentWeek) {
             status = "active";      // Semana actual (Verde, la única que se puede abrir)
          }

          return (
            <ClassAccordion 
              index={originalIndex} 
              data={clase} 
              status={status}
            />
          );
        })}
        
 
      </div>

    </div>
  );
};

export default CodigoFin;